import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import {
  Box,
  Stack,
  Select,
  Checkbox,
  MenuItem,
  Typography,
  InputLabel,
  FormControl,
  SelectChangeEvent,
  useTheme,
} from '@mui/material'
import cn from 'classnames'
import Button from '@mui/material/Button'
import FileSaver from 'file-saver'
import { toast } from 'react-toastify'
import ArrowButton from '@mui/icons-material/ArrowCircleRightOutlined'
import { ActionTypes } from '../map-entity'
import { useEmitters } from '../../../contexts/emitters.context'
import { analyzePoints, getHeatmap } from '../../../services/rf-model.service'
import { ICustomPoint } from '../../../types/emitter.type'
import prepareHeatmapParams from '../../../utils/prepareAreaRequestData'
import { getAffectedCountries, isHeatmapNeeded } from '../../../utils/functions'
import { IPathResponse } from '../../../../shared/types/api/heatmap.type'
import { IFrequencyAssignmentWithCoordinates } from '../../../types/exercise.type'

import styles from './styles.module.scss'

interface IProps {
  points: IFrequencyAssignmentWithCoordinates[]
  handleAct: (value: ActionTypes) => void
  actionType: string
  customPoints: ICustomPoint[]
  onPointSelect: (pointId: string) => void
  setCustomPoints: Dispatch<SetStateAction<ICustomPoint[]>>
  simulationLoading: boolean
  setIsPopupHovered: Dispatch<SetStateAction<boolean>>
  onOpenChange: (isOpen: boolean) => void
  handleSelectChange: (event: SelectChangeEvent<string>) => void
  handleResetLocation: () => void
  isDefaultPointsAdded: boolean
  showHeatmapSimulation: boolean
  handleAddDefaultPoints: () => void
  handleCustomPointsRemove: () => void
  onHeatmapSimulationClick: () => void
  setIsHeatmapParamsModalOpen: Dispatch<SetStateAction<boolean>>
  selectedFrequencyAssignment: IFrequencyAssignmentWithCoordinates
}

const EmitterDrawer: React.FC<IProps> = ({
  points,
  handleAct,
  actionType,
  customPoints,
  onPointSelect,
  setCustomPoints,
  simulationLoading,
  setIsPopupHovered,
  onOpenChange,
  handleSelectChange,
  handleResetLocation,
  isDefaultPointsAdded,
  showHeatmapSimulation,
  handleAddDefaultPoints,
  handleCustomPointsRemove,
  onHeatmapSimulationClick,
  setIsHeatmapParamsModalOpen,
  selectedFrequencyAssignment,
}) => {
  const theme = useTheme()

  const [isOpen, setIsOpen] = useState(true)
  const [pathData, setPathData] = useState<IPathResponse | null>(null)

  useEffect(() => {
    onOpenChange(isOpen)
  }, [isOpen, onOpenChange])

  const {
    fetchOneHeatmap,
    fetchHeatmapForEmitter,
    showingRadii,
    setShowRadius,
    showingHeatmaps,
    setShowHeatmap,
    setHeatmap,
  } = useEmitters()

  const handleShowRadius = (show: boolean, pointId?: string) => {
    if (pointId) {
      setCustomPoints((prev) => prev.map((elem) => (elem.id === pointId ? { ...elem, showRadius: show } : elem)))
    } else {
      setShowRadius(selectedFrequencyAssignment.id_override, show)
    }
  }

  const fetchHeatmap = async (point?: ICustomPoint) => {
    // Take a copy of this to avoid race conditions:
    const { id_override } = selectedFrequencyAssignment

    try {
      if (point) {
        // Get a heatmap for a custom point
        let isNewHeatmapNeeded = isHeatmapNeeded(point.latitude, point.longitude, point.heatmap)

        if (point && isNewHeatmapNeeded && point.radius) {
          const heatmap = await fetchOneHeatmap(point.id, 0, point.heatmapParams)
          let affectedCountries: Array<{ country: string; flag: string }> = getAffectedCountries(heatmap)

          setCustomPoints((prev) =>
            prev.map((elem) => (elem.id === point.id ? { ...elem, heatmap, affectedCountries } : elem)),
          )
        }
      } else {
        // Called when you click the regular Show Heatmap button.
        let isNewHeatmapNeeded = isHeatmapNeeded(
          selectedFrequencyAssignment.latitude,
          selectedFrequencyAssignment.longitude,
          selectedFrequencyAssignment.heatmaps?.[0],
        )
        if (
          selectedFrequencyAssignment &&
          isNewHeatmapNeeded &&
          selectedFrequencyAssignment.request_location_radius &&
          !selectedFrequencyAssignment.heatmapsLoading
        ) {
          await fetchHeatmapForEmitter({ id_override })
        }
      }
    } catch (err: any) {
      // fetchOneHeatmap (which is also called by fetchHeatmapForEmitter)
      // already shows a toast, so just log it and clean up:
      setHeatmap(id_override, 0, null, { heatmapsLoading: false })
      console.error(err)
    }
  }

  const handleAnalyzePoints = async () => {
    const selectedPoints = customPoints.filter((point) => point.selected)

    if (selectedPoints.length !== 2) {
      toast('Please choose 2 points for analysis')
      return
    }

    const firstPoint = selectedPoints[0]
    const secondPoint = selectedPoints[1]

    const path = await analyzePoints(
      prepareHeatmapParams(
        {
          alt: firstPoint.elevation,
          txw: firstPoint.power,
          frq: firstPoint.request_frequency_value,
          lat: firstPoint.latitude,
          lon: firstPoint.longitude,
          rad: firstPoint.radius,
        },
        {
          alt: 1,
          rxg: 3,
          rxs: -100,
          lat: secondPoint.latitude,
          lon: secondPoint.longitude,
        },
      ),
    )

    if (path.data) {
      setPathData(path.data)
    }
  }

  const handleExport = async (format: 'kml' | 'kmz') => {
    if (selectedFrequencyAssignment === null || !selectedFrequencyAssignment.heatmaps?.[0]?.kmz) return

    try {
      const blob = await getHeatmap(selectedFrequencyAssignment.heatmaps?.[0]?.id, format)
      FileSaver.saveAs(blob, `${selectedFrequencyAssignment.id_override}.${format}`)
    } catch (error) {
      console.error(`Error during ${format} export: `, error)
    }
  }

  const handleDownloadAnalysis = async () => {
    if (!pathData) return

    try {
      // Build a Blob from the base64'd image data:
      const byteString = atob(pathData.path_png)
      const arrayBuffer = new ArrayBuffer(byteString.length)
      const intArray = new Uint8Array(arrayBuffer)
      for (let i = 0; i < byteString.length; i++) {
        intArray[i] = byteString.charCodeAt(i)
      }
      const blob = new Blob([intArray], { type: 'image/png' })
      FileSaver.saveAs(blob, `${selectedFrequencyAssignment.id_override}.png`)
    } catch (error) {
      console.error(`Error during PNG export: `, error)
    }
  }

  const handleHeatmapClick = (show: boolean, point?: ICustomPoint) => {
    if (show) {
      fetchHeatmap(point)
    }

    if (point) {
      setCustomPoints((prev) => prev.map((elem) => (elem.id === point.id ? { ...elem, showHeatmap: show } : elem)))
    } else {
      setShowHeatmap(selectedFrequencyAssignment.id_override, show)
    }
  }

  useEffect(() => {
    setPathData(null)
  }, [customPoints.filter((point) => point.selected).length])

  const showingHeatmap = showingHeatmaps[selectedFrequencyAssignment.id_override]

  return (
    <div
      style={{
        background: theme.palette.background.default,
      }}
      className={`${styles.sidebar} sub-container`}
      onMouseOver={() => setIsPopupHovered(true)}
      onMouseLeave={() => setIsPopupHovered(false)}
    >
      <Stack className={cn(styles.box, { [styles.closed]: !isOpen })}>
        <Typography>{selectedFrequencyAssignment.exercise_name}</Typography>
        <Select
          MenuProps={{ style: { zIndex: 6010 } }}
          value={selectedFrequencyAssignment.id_override}
          onChange={handleSelectChange}
          disabled={selectedFrequencyAssignment.heatmapsLoading || simulationLoading}
        >
          {points.map((point) => (
            <MenuItem key={point.id_override} value={point.id_override}>
              {point.id_override}
            </MenuItem>
          ))}
        </Select>
        {(selectedFrequencyAssignment.heatmapsLoading || simulationLoading) && (
          <span style={{ marginLeft: '5px' }}>Loading...</span>
        )}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            marginTop: 10,
          }}
        >
          <div style={{ display: 'flex', gap: 12 }}>
            <Button
              className={styles.btn}
              variant="outlined"
              onClick={() => handleShowRadius(!showingRadii[selectedFrequencyAssignment.id_override])}
            >
              {showingRadii[selectedFrequencyAssignment.id_override] ? 'Hide' : 'Show'} Radius
            </Button>
            <Button className={styles.btn} variant="outlined" onClick={() => handleHeatmapClick(!showingHeatmap)}>
              {showingHeatmap ? 'Hide' : 'Show'} Heatmap
            </Button>
            <Button className={styles.btn} variant="outlined" onClick={() => onHeatmapSimulationClick()}>
              {showHeatmapSimulation ? 'Hide' : 'Show'} Heatmap Simulation
            </Button>
          </div>
        </div>
        <div>
          <div>
            <div style={{ display: 'flex', gap: 12, paddingBottom: 15 }}>
              <Button variant="outlined" className={styles.btn} onClick={() => setIsHeatmapParamsModalOpen(true)}>
                Change heatmap params
              </Button>
            </div>
            <p>Export as:</p>
            <div style={{ display: 'flex', gap: 12 }}>
              <Button
                variant="outlined"
                disabled={!selectedFrequencyAssignment.heatmaps?.[0]?.kmz}
                onClick={() => handleExport('kml')}
              >
                KML
              </Button>
              <Button
                variant="outlined"
                disabled={!selectedFrequencyAssignment.heatmaps?.[0]?.kmz}
                onClick={() => handleExport('kmz')}
              >
                KMZ
              </Button>
            </div>
          </div>
        </div>
        <Box className={styles.actionsContainer}>
          <FormControl>
            <InputLabel id="actions">Actions</InputLabel>
            <Select
              labelId="actions"
              value={actionType}
              onChange={(evt) => handleAct(evt.target.value as ActionTypes)}
              label="Actions"
            >
              <MenuItem value={ActionTypes.NONE}>None</MenuItem>
              <MenuItem value={ActionTypes.MOVE}>Move Emitter</MenuItem>
              <MenuItem value={ActionTypes.ADD}>Add custom points</MenuItem>
              <MenuItem value={ActionTypes.ANALYZE}>Analyze Points</MenuItem>
            </Select>
          </FormControl>
          {selectedFrequencyAssignment.latitude !== selectedFrequencyAssignment.initialLat &&
            selectedFrequencyAssignment.longitude !== selectedFrequencyAssignment.initialLong && (
              <Button variant="outlined" onClick={handleResetLocation}>
                {'Reset'}
              </Button>
            )}
          <Box>
            {actionType === ActionTypes.ADD && !isDefaultPointsAdded && (
              <Button variant="outlined" onClick={handleAddDefaultPoints} className={styles.btn}>
                Add default points
              </Button>
            )}
            {isDefaultPointsAdded && (
              <Button className={styles.btn} onClick={handleCustomPointsRemove}>
                Remove points
              </Button>
            )}
          </Box>
        </Box>
        <Box
          className={cn(styles.arrowButton, { [styles.closed]: !isOpen })}
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <ArrowButton className={styles.icon} />
        </Box>
        {actionType === ActionTypes.ANALYZE && (
          <Box>
            <Typography>Please choose 2 points to analyze</Typography>
            <Box sx={{ display: 'flex', gap: '10px' }}>
              <Button
                onClick={handleAnalyzePoints}
                disabled={customPoints.filter((point) => point.selected).length !== 2}
              >
                Analyze
              </Button>
              {pathData && <Button onClick={handleDownloadAnalysis}>Download Analysis</Button>}
            </Box>
          </Box>
        )}
        {!!customPoints.length && <Typography>Manage Points</Typography>}
        <Box className={styles.customPoints}>
          {customPoints.map((point, index) => (
            <Box className={styles.customPointContainer} key={point.id}>
              <Typography className={styles.title}>Point N{index + 1}</Typography>
              <Box className={styles.actions}>
                <Checkbox checked={point.selected} onClick={() => onPointSelect(point.id)} />
                <Box className={styles.btns}>
                  <Button className={styles.btn} onClick={() => handleShowRadius(!point.showRadius, point.id)}>
                    {point.showRadius ? 'Hide' : 'Show'} Radius
                  </Button>
                  <Button className={styles.btn} onClick={() => handleHeatmapClick(!point.showHeatmap, point)}>
                    {point.showHeatmap ? 'Hide' : 'Show'} Heatmap
                  </Button>
                  <Button
                    className={styles.btn}
                    onClick={() =>
                      setCustomPoints((prev) =>
                        prev.map((elem) => (elem.id === point.id ? { ...elem, showHeatmapSettings: true } : elem)),
                      )
                    }
                  >
                    Heatmap Settings
                  </Button>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Stack>
    </div>
  )
}

export default EmitterDrawer
